/**
 * Dashboard — Analyzer-first version.
 * No dollar amounts. Shows "analysis progress" stats:
 *   - # projects in current module
 *   - # with plans uploaded
 *   - # with site survey done
 *   - # with analyzer state saved (configured)
 *   - recent projects with per-project progress indicator
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FolderKanban, FileText, ClipboardList, CheckCircle2, Plus, Sparkles,
  ShieldCheck, MessageSquare, Clock, Inbox, Building2,
} from "lucide-react";
import api from "@/lib/api";
import { useModule } from "@/context/ModuleContext";

const StatCard = ({ icon: Icon, label, value, accent = "blue", testid }) => (
  <div data-testid={testid}
       className="bg-white border border-slate-200 rounded-2xl p-5 flex items-start justify-between shadow-sm hover:shadow-md transition-shadow">
    <div>
      <div className="text-xs font-mono uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-2 text-3xl font-mono font-semibold text-slate-900 tracking-tight">{value}</div>
    </div>
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
      accent === "blue" ? "bg-[#003366]/10 text-[#003366]" :
      accent === "orange" ? "bg-[#EA580C]/10 text-[#EA580C]" :
      accent === "green" ? "bg-green-50 text-green-700" :
      "bg-slate-100 text-slate-700"
    }`}>
      {Icon && <Icon size={18} />}
    </div>
  </div>
);

function progressOf(p) {
  const hasPlan = p.file_count > 0;
  const hasSurvey = !!p.has_survey;
  const hasAnalyzer = !!p.analyzer_state;
  const done = [hasPlan, hasSurvey, hasAnalyzer].filter(Boolean).length;
  return Math.round((done / 3) * 100);
}

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const { module, meta } = useModule();
  const es = i18n.language?.startsWith("es");
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approvals, setApprovals] = useState({ counts: { pending:0, approved:0, commented:0, expired:0 }, items: [] });
  const [crmInbound, setCrmInbound] = useState({ count: 0, items: [] });

  useEffect(() => {
    api.get("/projects")
      .then(r => setProjects(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    api.get(`/dashboard/approvals?module=${encodeURIComponent(module)}`)
      .then(r => setApprovals(r.data || approvals))
      .catch(() => {});
    api.get("/integrations/crm/inbound-projects")
      .then(r => setCrmInbound(r.data || { count: 0, items: [] }))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module]);

  // Filter by current module
  const filtered = projects.filter(p => !p.module || p.module === "all" || p.module === module);

  const total = filtered.length;
  const withPlans = filtered.filter(p => p.file_count > 0).length;
  const withSurvey = filtered.filter(p => p.has_survey).length;
  const configured = filtered.filter(p => p.analyzer_state).length;

  return (
    <div className="p-6" data-testid="dashboard-page">
      <header className="flex items-start justify-between mb-6">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em]" style={{ color: meta.color }}>
            <span className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle"
                  style={{ backgroundColor: meta.color }} />
            {es ? meta.labelEs : meta.labelEn}
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 mt-1">
            {es ? "Analizador de Proyectos" : "Project Analyzer"}
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            {es ? "Sube planos, modifica, configura y entrega."
                : "Upload plans, modify, configure and deliver."}
          </p>
        </div>
        <Link to="/projects/new" data-testid="new-project-btn"
              className="bg-[#003366] text-white hover:bg-[#002244] rounded-xl px-5 py-2.5 font-medium text-sm transition-all inline-flex items-center gap-2 shadow-sm hover:shadow-md">
          <Plus size={14} /> {es ? "Nuevo Proyecto" : "New Project"}
        </Link>
      </header>

      {/* Stats — NO DOLLAR AMOUNTS, only analysis progress */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={FolderKanban}  label={es?"Proyectos":"Projects"}  value={total} accent="blue" testid="stat-projects" />
        <StatCard icon={FileText}      label={es?"Con planos":"With plans"} value={withPlans} accent="blue" testid="stat-with-plans" />
        <StatCard icon={ClipboardList} label={es?"Con survey":"With survey"} value={withSurvey} accent="orange" testid="stat-with-survey" />
        <StatCard icon={CheckCircle2}  label={es?"Configurados":"Configured"} value={configured} accent="green" testid="stat-configured" />
      </section>

      {/* CRM Inbound widget — proyectos creados automáticamente desde el CRM */}
      <section className="bg-white border border-slate-200 rounded-2xl shadow-sm mb-6 overflow-hidden"
               data-testid="crm-inbound-widget">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-semibold text-slate-900 flex items-center gap-2">
            <Inbox size={16} className="text-[#003366]" />
            {es ? "Proyectos recibidos del CRM" : "Projects received from CRM"}
            {crmInbound.count > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2
                               bg-[#EA580C] text-white text-xs font-mono font-semibold rounded-full"
                    data-testid="crm-inbound-badge">
                {crmInbound.count}
              </span>
            )}
          </h2>
          <span className="text-xs font-mono text-slate-500">
            {es ? "Auto-creados vía webhook" : "Auto-created via webhook"}
          </span>
        </div>
        {crmInbound.items.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="no-crm-inbound">
            {es ? "Sin proyectos entrantes del CRM. Cuando el CRM dispare un webhook"
                  + " blueprint.uploaded el proyecto aparecerá aquí."
                : "No inbound CRM projects. When the CRM fires a blueprint.uploaded"
                  + " webhook, projects will appear here."}
          </div>
        ) : (
          <ul data-testid="crm-inbound-list">
            {crmInbound.items.slice(0, 5).map(p => (
              <li key={p.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 px-4 py-3">
                <Link to={`/projects/${p.id}/analyzer`}
                      className="flex items-center justify-between gap-3"
                      data-testid={`crm-inbound-row-${p.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-900 truncate flex items-center gap-2">
                      <Building2 size={12} className="text-slate-400 flex-shrink-0" />
                      {p.name}
                    </div>
                    <div className="text-xs font-mono text-slate-500 truncate">
                      {p.client || "—"} · client_id: {(p.crm_client_id || "—").slice(0, 8)}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-[10px] font-mono uppercase tracking-wider border rounded-full
                                     px-2 py-0.5 bg-amber-50 text-amber-800 border-amber-200">
                      {es ? "Pendiente análisis" : "Pending analysis"}
                    </span>
                    <Sparkles size={14} className="text-[#EA580C]" />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Approvals widget */}
      <section className="bg-white border border-slate-200 rounded-2xl shadow-sm mb-6 overflow-hidden"
               data-testid="approvals-widget">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-semibold text-slate-900 flex items-center gap-2">
            <ShieldCheck size={16} className="text-emerald-600" />
            {es ? "Aprobaciones de clientes" : "Client approvals"}
          </h2>
          <div className="flex items-center gap-2 flex-wrap">
            <Pill color="emerald" icon={CheckCircle2} label={es?"Aprobados":"Approved"} value={approvals.counts.approved} testid="pill-approved" />
            <Pill color="blue"    icon={MessageSquare} label={es?"Comentados":"Commented"} value={approvals.counts.commented} testid="pill-commented" />
            <Pill color="amber"   icon={Clock} label={es?"Pendientes":"Pending"} value={approvals.counts.pending} testid="pill-pending" />
            <Pill color="slate"   icon={Clock} label={es?"Expirados":"Expired"} value={approvals.counts.expired} testid="pill-expired" />
          </div>
        </div>
        {approvals.items.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="no-approvals">
            {es ? "Aún no se han generado links públicos. Ve al Analyzer → Paso 4 → \"Link al cliente\"."
                : "No public links generated yet. Go to Analyzer → Step 4 → \"Client link\"."}
          </div>
        ) : (
          <ul data-testid="approvals-list">
            {approvals.items.slice(0, 6).map(a => (
              <li key={a.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 px-4 py-3">
                <Link to={`/projects/${a.project_id}/analyzer`}
                      className="flex items-center justify-between gap-3"
                      data-testid={`approval-row-${a.project_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-900 truncate">{a.project_name || "—"}</div>
                    <div className="text-xs font-mono text-slate-500 truncate">
                      {a.company || "—"} · {a.contact_email || "—"}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <StatusBadge status={a.status} es={es} />
                    <span className="text-xs font-mono text-slate-400 hidden md:inline">
                      {a.feedback_count} {es?"feedbacks":"feedbacks"}
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Recent projects with progress bars */}
      <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">
            {es ? "Proyectos recientes" : "Recent projects"}
          </h2>
          <Link to="/projects" className="text-xs font-mono uppercase tracking-wider text-[#003366] hover:underline">
            {es ? "Ver todos" : "View all"} →
          </Link>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="no-projects">
            {es ? "No hay proyectos aún en este módulo." : "No projects yet in this module."}
          </div>
        ) : (
          <ul data-testid="recent-projects-list">
            {filtered.slice(0, 8).map(p => {
              const pct = progressOf(p);
              return (
                <li key={p.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 px-4 py-3">
                  <Link to={`/projects/${p.id}/analyzer`} className="flex items-center justify-between gap-4" data-testid={`dash-project-${p.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-900 truncate">{p.name}</div>
                      <div className="text-xs font-mono text-slate-500 truncate">
                        {p.company || p.client || "—"} · {p.location}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="w-28 h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full bg-[#003366]" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs font-mono text-slate-600 w-10 text-right">{pct}%</span>
                      <Sparkles size={14} className="text-[#EA580C]" />
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

const PILL_COLORS = {
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  blue:    "bg-[#003366]/10 text-[#003366] border-[#003366]/20",
  amber:   "bg-amber-50 text-amber-800 border-amber-200",
  slate:   "bg-slate-100 text-slate-600 border-slate-200",
};

function Pill({ color, icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid}
         className={`inline-flex items-center gap-1.5 border rounded-full px-2.5 py-1 text-xs font-mono ${PILL_COLORS[color] || PILL_COLORS.slate}`}>
      <Icon size={12} />
      <span className="uppercase tracking-wider">{label}</span>
      <span className="font-bold ml-0.5">{value}</span>
    </div>
  );
}

function StatusBadge({ status, es }) {
  const map = {
    approved:  { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: es?"Aprobado":"Approved" },
    commented: { cls: "bg-[#003366]/10 text-[#003366] border-[#003366]/30", label: es?"Comentado":"Commented" },
    pending:   { cls: "bg-amber-50 text-amber-800 border-amber-200", label: es?"Pendiente":"Pending" },
    expired:   { cls: "bg-slate-100 text-slate-600 border-slate-200", label: es?"Expirado":"Expired" },
  };
  const s = map[status] || map.pending;
  return (
    <span className={`text-[10px] font-mono uppercase tracking-wider border rounded-full px-2 py-0.5 ${s.cls}`}>
      {s.label}
    </span>
  );
}

